/**
 * Degrees / decimal-minutes coordinate helpers for the committee chart mark
 * editor (ra-gbrd.30).
 *
 * Race marks are positioned on paper sailing instructions in nautical
 * "degrees + decimal minutes" (e.g. 53° 17.110' N), so the chart editor reads
 * out and arrow-key nudges in that unit rather than raw decimal degrees. These
 * helpers are deliberately pure (no DOM, no Leaflet) so they're unit-testable.
 */

/** One arc-minute expressed in decimal degrees. */
export const MINUTE_DEG = 1 / 60;

/**
 * Format a signed decimal degree as nautical degrees + decimal minutes with a
 * hemisphere letter.
 *
 *   formatDms(53.285, 'lat')   => "53° 17.100' N"
 *   formatDms(-6.002, 'lng')   => "6° 00.120' W"
 *
 * @param {number} decimal signed decimal degrees (negative = S / W)
 * @param {'lat'|'lng'} axis which hemisphere letter pair to use
 * @param {number} [minDecimals=3] decimal places on the minutes component
 * @returns {string} formatted coordinate, or "—" for a non-finite input
 */
export function formatDms(decimal, axis, minDecimals = 3) {
  if (typeof decimal !== 'number' || !Number.isFinite(decimal)) return '—';
  const hemis = axis === 'lat' ? (decimal < 0 ? 'S' : 'N') : (decimal < 0 ? 'W' : 'E');
  const abs = Math.abs(decimal);
  let deg = Math.floor(abs);
  const factor = 10 ** minDecimals;
  let min = Math.round((abs - deg) * 60 * factor) / factor;
  // Rounding the minutes can roll 59.9996' up to 60.000' — carry into degrees.
  if (min >= 60) {
    min -= 60;
    deg += 1;
  }
  // Zero-pad the whole-minute part to two digits (nautical convention: 03.000').
  const width = minDecimals > 0 ? 3 + minDecimals : 2;
  const minStr = min.toFixed(minDecimals).padStart(width, '0');
  return `${deg}° ${minStr}' ${hemis}`;
}

/**
 * Nudge a decimal-degree coordinate by a (possibly fractional or negative)
 * number of arc-minutes. Returns decimal degrees.
 *
 * @param {number} decimal signed decimal degrees
 * @param {number} minutes arc-minutes to add (negative subtracts)
 * @returns {number} new decimal degrees
 */
export function nudgeMinutes(decimal, minutes) {
  return decimal + minutes * MINUTE_DEG;
}
